import styles from "./Drawer.module.scss";
import {
  ComponentPropsWithoutRef,
  forwardRef,
  useRef,
  useId,
  useCallback,
  useEffect,
  SyntheticEvent,
  KeyboardEvent,
  useMemo,
  useLayoutEffect,
} from "react";
import { DrawerHeader } from "./DrawerHeader";
import { DrawerFooter } from "./DrawerFooter";
import { DrawerContent } from "./DrawerContent";
import { DrawerCancelButton } from "./DrawerCancelButton";
import {
  OpenCloseTransitionStateEffects,
  useDialogScrollLock,
  useDialogTransitionStates,
  useKeyboardFocusables,
  useOnClickOutside,
} from "../../internal/hooks";
import {
  FocusableElement,
  DisableCloseOnEscapeOrClickProps,
  Size,
} from "../../types";
import { registerLayer, unregisterLayer } from "../../internal/utils";
import { useMergeRefs } from "../../hooks";
import cx from "classnames";
import { ToastProvider } from "../Toast/internal/ToastProvider";
import { ToasterPopover } from "../Toast/internal/ToasterPopover";
import { ToasterContent } from "../Toast/internal/Toaster";
import { DrawerContext } from "./internal/DrawerContext";
import {
  DrillDownContext,
  DrillDownContextProps,
} from "../DrillDown/internal/DrillDownContext";
import { useDrillDownContextState } from "../DrillDown/internal/useDrillDownContextState";
import drillDownStyles from "../DrillDown/DrillDown.module.scss";
import { useInitialFocus } from "../Dialog/internal/useInitialFocus";

/**
 * Props for the Drawer component
 * @extends ComponentPropsWithoutRef<"dialog">
 * @extends DialogTransitionStateEffects
 * @extends DisableCloseOnEscapeOrClickProps
 */
export type DrawerProps = ComponentPropsWithoutRef<"dialog"> &
  OpenCloseTransitionStateEffects &
  DisableCloseOnEscapeOrClickProps & {
    /**
     * The size of the drawer.
     * @default "medium"
     */
    size?: Extract<Size, "small" | "medium" | "large" | "xlarge">;
    /**
     * Controls whether the drawer is open or closed.
     * @default false
     */
    open?: boolean;
    /**
     * Callback function called after the drawer is animated closed.
     */
    onClose?: () => void;
    /**
     * Callback when clicking outside the drawer
     */
    onClickOutside?: (e: globalThis.MouseEvent) => void;
    /**
     * Enables scroll chaining behavior.
     * @link https://developer.mozilla.org/en-US/docs/Glossary/Scroll_chaining
     */
    enableScrollChaining?: boolean;
    /**
     * Given an array of focusable elements, return the initial focus target.
     * @param focusables - The focusable elements to pick from
     * @returns The initial focus target
     */
    initialFocusResolver?: (focusables: FocusableElement[]) => FocusableElement;
    /**
     * Sets the default DrillDown index when used in an uncontrolled way
     */
    defaultDrillDownIndex?: DrillDownContextProps["index"];
    /**
     * Callback to pass context to useDrillDown hook
     */
    onDrillDownContextChange?: (context: DrillDownContextProps) => void;
  };

/**
 * A slide-out drawer component that provides a modal-like experience from the side of the screen.
 *
 * Features:
 * - Slide-in animation from the side of the screen
 * - Keyboard navigation with focus trapping
 * - Escape key and click outside to close
 * - Configurable size variants (medium, large)
 * - Built-in toast support for notifications
 * - Automatic scroll locking when open
 * - Accessible with proper ARIA attributes
 * - Compound component structure with Header, Footer, Content, and CancelButton
 * - Transition state callbacks for animation control
 * - Optional scroll chaining support
 * - Automatic tracking ID generation for analytics
 *
 * @example
 * <Drawer open={isOpen} onClose={handleClose} size="medium">
 *   <Drawer.Header>Settings</Drawer.Header>
 *   <Drawer.Content>Drawer content goes here</Drawer.Content>
 *   <Drawer.Footer>
 *     <Button onClick={handleSave}>Save</Button>
 *     <Drawer.CancelButton>Cancel</Drawer.CancelButton>
 *   </Drawer.Footer>
 * </Drawer>
 */
export const Drawer = Object.assign(
  forwardRef<HTMLDialogElement, DrawerProps>(function DrawerInner(props, ref) {
    const {
      className,
      open = false,
      children,
      onClose,
      // onCancel is destructured so that it is not passed to the dialog in ...rest
      onCancel,
      onKeyDown,
      disableCloseOnEscape = false,
      onClickOutside,
      disableCloseOnClickOutside = false,
      size = "medium",
      enableScrollChaining,
      onOpenAnimationStart,
      onOpenAnimationComplete,
      onCloseAnimationStart,
      onCloseAnimationComplete,
      initialFocusResolver,
      defaultDrillDownIndex,
      onDrillDownContextChange,
      ...rest
    } = props;

    const elRef = useRef<HTMLDialogElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const toasterRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLElement>(null);
    const combinedRef = useMergeRefs([elRef, ref]);

    const drillDownContextValue = useDrillDownContextState({
      defaultDrillDownIndex,
    });

    const id = useId();

    const { state, isOpenOrOpening } = useDialogTransitionStates(elRef, open, {
      onOpenAnimationStart: useCallback(() => {
        onOpenAnimationStart?.();
      }, [onOpenAnimationStart]),
      onOpenAnimationComplete: useCallback(() => {
        onOpenAnimationComplete?.();
      }, [onOpenAnimationComplete]),
      onCloseAnimationStart: useCallback(() => {
        onCloseAnimationStart?.();
      }, [onCloseAnimationStart]),
      onCloseAnimationComplete: useCallback(() => {
        onCloseAnimationComplete?.();
      }, [onCloseAnimationComplete]),
    });

    const previousOpenRef = useRef(open);

    useLayoutEffect(() => {
      if (open && !previousOpenRef.current) {
        drillDownContextValue.setHostTrigger?.(
          document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null,
        );
      }
    }, [open, drillDownContextValue]);

    useEffect(() => {
      previousOpenRef.current = open;
    }, [open]);

    useEffect(() => {
      if (state === "closing") {
        drillDownContextValue.onHostClose?.();
      }
    }, [state, drillDownContextValue]);

    useEffect(() => {
      if (state === "closed") {
        drillDownContextValue.onHostClosed?.();
      }
    }, [state, drillDownContextValue]);

    const drillDownContextProviderValue = useMemo(
      () => ({
        ...drillDownContextValue,
        hostIsInteractive: state !== "closing" && state !== "closed",
      }),
      [drillDownContextValue, state],
    );

    useLayoutEffect(() => {
      onDrillDownContextChange?.(drillDownContextProviderValue);
    }, [drillDownContextProviderValue]); // eslint-disable-line react-hooks/exhaustive-deps

    const classNames = cx([styles.drawer, drillDownStyles.drawer], className, {
      [styles.remove]: state === "closing",
      [styles[size]]: true,
    });

    const { focusables } = useKeyboardFocusables(contentRef, {
      observeChange: false,
    });
    const firstFocusable = focusables?.[0];
    const lastFocusable = focusables?.[focusables.length - 1];

    useDialogScrollLock({
      open: isOpenOrOpening,
      id,
      enableScrollChaining,
      elementRef: elRef,
    });

    // Register/unregister with layer manager for proper stacking
    useEffect(() => {
      if (open) {
        registerLayer(id);
      } else {
        unregisterLayer(id);
      }

      // Cleanup when component unmounts
      return () => {
        unregisterLayer(id);
      };
    }, [open, id]);

    const computeFocus = useCallback((focusables: FocusableElement[]) => {
      return (
        focusables.find(
          (focusable) => focusable.dataset.anv !== "drawer-header-close-button",
        ) || focusables[0]
      );
    }, []);

    useInitialFocus(elRef, {
      computeFocus: initialFocusResolver || computeFocus,
    });

    const onCancelHandler = (e: SyntheticEvent<HTMLDialogElement>) => {
      e.preventDefault();

      if (disableCloseOnEscape) return;
      onClose?.();
    };

    useOnClickOutside({
      onClickOutsideBegin: (e) => {
        if (!open) return;
        onClickOutside?.(e);
      },
      targets: [
        contentRef.current as HTMLElement,
        toasterRef.current as HTMLElement,
      ],
      onClickOutside: () => {
        if (disableCloseOnClickOutside || !open) return;
        onClose?.();
      },
      layerId: id,
    });

    const onKeyDownHandler = (e: KeyboardEvent<HTMLDialogElement>) => {
      onKeyDown?.(e);
      if (e.code === "Tab") {
        if (e.shiftKey && e.target === firstFocusable) {
          e.preventDefault();
          (lastFocusable as HTMLElement)?.focus();
          return;
        }
        if (e.target === lastFocusable && !e.shiftKey) {
          e.preventDefault();
          (firstFocusable as HTMLElement)?.focus();
          return;
        }
      }
    };

    const contextValue = useMemo(() => {
      return { close: () => onClose?.(), headerRef, state };
    }, [onClose, state]);

    return (
      // dialog element does seem to be interactive on MDN guideline
      <dialog
        className={classNames}
        data-anv="drawer"
        data-anv-scroll-chaining={enableScrollChaining}
        data-anv-transition-state={state}
        ref={combinedRef}
        onCancel={onCancelHandler}
        onKeyDown={onKeyDownHandler}
        {...rest}
      >
        <ToastProvider isDialogToaster={isOpenOrOpening}>
          <ToasterPopover>
            <ToasterContent id="drawer-toaster" ref={toasterRef} />
          </ToasterPopover>
        </ToastProvider>

        <DrillDownContext.Provider value={drillDownContextProviderValue}>
          <DrawerContext.Provider value={contextValue}>
            <section
              ref={contentRef}
              className={styles.container}
              data-open={isOpenOrOpening}
            >
              {children}
            </section>
          </DrawerContext.Provider>
        </DrillDownContext.Provider>
      </dialog>
    );
  }),
  {
    /**
     * Header component for the drawer that displays a title and close button.
     *
     * Features:
     * - Displays the drawer title as a headline
     * - Includes an accessible close button
     * - Sticky positioning at the top of the drawer
     * - Proper semantic HTML structure
     * - Accessible with proper ARIA attributes
     * - Consistent styling with the design system
     * - Automatic close functionality
     * - Automatic tracking ID generation for analytics
     *
     * @example
     * <Drawer.Header>Settings</Drawer.Header>
     */
    Header: DrawerHeader,
    /**
     * Footer component for the drawer that displays action buttons and controls.
     *
     * Features:
     * - Flexible layout for action buttons and controls
     * - Optional sticky positioning at the bottom
     * - Proper semantic HTML structure
     * - Accessible with proper ARIA attributes
     * - Consistent styling with the design system
     * - Supports all standard footer element props
     * - Responsive design that adapts to content
     *
     * @example
     * <Drawer.Footer sticky>
     *   <Button onClick={handleSave}>Save</Button>
     *   <Drawer.CancelButton>Cancel</Drawer.CancelButton>
     * </Drawer.Footer>
     */
    Footer: DrawerFooter,
    /**
     * Content container for the drawer that holds the main content area.
     *
     * Features:
     * - Flexible content area with proper spacing
     * - Scrollable content when needed
     * - Proper semantic HTML structure
     * - Accessible with proper ARIA attributes
     * - Consistent styling with the design system
     * - Supports all standard div element props
     * - Responsive design that adapts to content
     * - Proper focus management
     *
     * @example
     * <Drawer.Content>
     *   <p>This is the main content of the drawer.</p>
     *   <p>It can contain any type of content.</p>
     * </Drawer.Content>
     */
    Content: DrawerContent,
    /**
     * A button component that automatically closes the drawer when clicked.
     *
     * Features:
     * - Automatically closes the drawer when clicked
     * - Extends Button component functionality
     * - Accessible with proper ARIA attributes
     * - Consistent styling with the design system
     * - Supports all standard button props
     * - Proper focus management
     * - Can be used anywhere within the drawer context
     * - Automatic tracking ID generation for analytics
     *
     * @example
     * <Drawer.CancelButton appearance="ghost" size="small">
     *   Cancel
     * </Drawer.CancelButton>
     */
    CancelButton: DrawerCancelButton,
  },
);
Drawer.displayName = "Drawer";
